import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Continent, continentOf, globalNetworks, GLOBAL_NETWORKS_AS_OF } from '../data/globalNetworks';
import { aspopUrl } from '../utils/apnic';
import { formatCompact, formatInt } from '../utils/format';
import { useCountryData } from '../hooks/useCountryData';
import { PageHeader } from '../components/PageHeader';
import { DataBadge } from '../components/DataBadge';
import { SourceRef } from '../components/SourceRef';
import { ChipGroup } from '../components/ChipGroup';
const CONTINENTS: Continent[] = ['Asia', 'Americas', 'Europe', 'Africa', 'Middle East', 'Oceania'];
type Filter = 'all' | Continent;
export function Networks() {
  const data = useCountryData();
  const [filter, setFilter] = useState<Filter>('all');
  const netUsersByCountry = useMemo(() => {
    const m: Record<string, number> = {};
    data.rows.forEach(r => r.internetUsers != null && (m[r.cca3] = r.internetUsers));
    return m;
  }, [data.rows]);
  const ranked = useMemo(() => [...globalNetworks].sort((a, b) => b.users - a.users), []);
  const rows = filter === 'all' ? ranked : ranked.filter(n => continentOf[n.cca3] === filter);
  const max = Math.max(...rows.map(n => n.users), 1);
  const countries = new Set(globalNetworks.map(n => n.cca3)).size;
  const byContinent = useMemo(() => CONTINENTS.map(c => {
    const list = ranked.filter(n => continentOf[n.cca3] === c);
    return {
      continent: c,
      count: list.length,
      users: list.reduce((s, n) => s + n.users, 0),
      top: list[0]
    };
  }), [ranked]);
  const totalUsers = byContinent.reduce((s, c) => s + c.users, 0);
  return <>
      <PageHeader title="Who Carries the Internet" lede="The largest networks on every continent, ranked by APNIC’s estimate of the people behind them. A handful of Asian mobile carriers each serve more users than whole continents’ biggest operators." data-id="element-1346" data-mp="2bdft2ki9ag1u">
        <DataBadge mode="snapshot" source="APNIC aspop · global networks" asOf={GLOBAL_NETWORKS_AS_OF} data-id="element-1347" data-mp="9jothnpisxyr" />
      </PageHeader>

      <section className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-1348" data-mp="xhehkef7ouji">
        <dl className="grid grid-cols-2 border-b border-line md:grid-cols-4" data-id="element-1349" data-mp="xiz8lu2bhvcm">
          {[{
          k: 'Networks tracked',
          v: String(globalNetworks.length)
        }, {
          k: 'Countries covered',
          v: String(countries)
        }, {
          k: 'Users they carry',
          v: formatCompact(totalUsers)
        }, {
          k: 'Snapshot date',
          v: GLOBAL_NETWORKS_AS_OF
        }].map((s, i) => <div key={s.k} className={`py-5 ${i > 0 ? 'md:border-l md:border-line md:pl-6' : ''} ${i % 2 === 1 ? 'border-l border-line pl-4 md:pl-6' : ''}`} data-id="element-1350" data-mp="1gexq7gxrcb5e" data-mpi={i}>
              <dt className="text-[12.5px] text-muted" data-id="element-1351" data-mp="17e9bek0ji6rg">{s.k}</dt>
              <dd className="mt-1 font-mono text-2xl tabular text-ink" data-id="element-1352" data-mp="3n66qjgucx25e">
                {s.v}
                <SourceRef id="apnicAspop" estimate={s.k === 'Users they carry' ? {
              low: formatCompact(totalUsers * 0.85),
              high: formatCompact(totalUsers * 1.15)
            } : undefined} data-id="element-1353" data-mp="3s59vl1ac78dd" />
              </dd>
            </div>)}
        </dl>

        <section aria-labelledby="continent-title" className="mt-10" data-id="element-1354" data-mp="3flmbtjcrbnn4">
          <h2 id="continent-title" className="font-serif text-2xl text-ink" data-id="element-1355" data-mp="3tm6w6jyblriq">
            By continent
          </h2>
          <ul className="mt-4 grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1356" data-mp="3on419sc211oh">
            {byContinent.map(c => <li key={c.continent} data-id="element-1357" data-mp="30ykuxickeyz1">
                <button type="button" onClick={() => setFilter(filter === c.continent ? 'all' : c.continent)} aria-pressed={filter === c.continent} className={`w-full border-t-2 pt-3 text-left transition-colors duration-150 ${filter === c.continent ? 'border-machine' : 'border-line hover:border-faint'}`} data-id="element-1358" data-mp="30vf1jqwwlaxi">
                  <p className="flex items-baseline justify-between" data-id="element-1359" data-mp="2i4xcbwba4xgw">
                    <span className="text-[15px] text-ink" data-id="element-1360" data-mp="2d7nq0pj6t1xa">{c.continent}</span>
                    <span className="font-mono text-[13px] tabular text-human" data-id="element-1361" data-mp="2qvywf17v480w">{formatCompact(c.users)}</span>
                  </p>
                  <div className="mt-2 h-1.5 rounded-full bg-raised" aria-hidden="true" data-id="element-1362" data-mp="2vua71e5zemk6">
                    <div className="h-full rounded-full bg-human/75" style={{
                  width: `${c.users / Math.max(1, byContinent[0].users) * 100}%`
                }} data-id="element-1363" data-mp="30riunu9wfist" />
                  </div>
                  <p className="mt-2 truncate text-[12.5px] text-muted" data-id="element-1364" data-mp="39m6z5i7ngwgk">
                    {c.count} networks · largest {c.top ? `${c.top.name} (${c.top.country})` : '—'}
                  </p>
                </button>
              </li>)}
          </ul>
        </section>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3" data-id="element-1365" data-mp="3oj7qhklxipei">
          <ChipGroup label="Continent" value={filter} onChange={setFilter} options={[['all', 'All'], ...CONTINENTS.map(c => [c, c] as [Filter, string])]} data-id="element-1366" data-mp="1p5k5g0skpbth" />
          <span className="font-mono text-[12px] text-muted" data-id="element-1367" data-mp="1u56q2vaqducb">{rows.length} networks</span>
        </div>

        <div className="mt-4 overflow-x-auto rounded-lg border border-line" data-id="element-1368" data-mp="batullnoghy1">
          <table className="w-full min-w-[960px] border-collapse text-[13.5px]" data-id="element-1369" data-mp="6i0pgwoar7lf">
            <thead className="bg-surface text-left text-[12px] text-muted" data-id="element-1370" data-mp="2gfifryg1nj5">
              <tr data-id="element-1371" data-mp="3tnrzm6zbu0z2">
                <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-normal" data-id="element-1372" data-mp="3tob7euv9j8zd">#</th>
                <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-1373" data-mp="u8o92f7krlnt">ASN</th>
                <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-1374" data-mp="q6jkvvzwbh8k">Operator</th>
                <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-1375" data-mp="l6nfptfgpjrf">Country</th>
                <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-normal" data-id="element-1376" data-mp="g7k9w6rnr9ie">
                  Share of country’s internet users
                  <SourceRef id="wbInternet" data-id="element-1377" data-mp="35vkzcwqhd9ud" />
                </th>
                <th scope="col" className="w-[30%] border-b border-line px-3 py-2.5 font-normal" data-id="element-1378" data-mp="30x9oqjsd2vb3">Est. users</th>
              </tr>
            </thead>
            <tbody data-id="element-1379" data-mp="37eumz6z2wtjn">
              {rows.map((n, i) => {
              const net = netUsersByCountry[n.cca3];
              const share = net ? Math.min(100, n.users / net * 100) : null;
              return <tr key={n.asn} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-1380" data-mp="2ydmzljy3pd4c" data-mpi={i}>
                    <td className="px-3 py-2.5 text-right font-mono text-[12px] text-muted" data-id="element-1381" data-mp="2joh4a72dw71q">{i + 1}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[12.5px] text-human" data-id="element-1382" data-mp="2oha72peal5b6">{n.asn}</td>
                    <td className="px-3 py-2.5 text-ink" data-id="element-1383" data-mp="2aqexfavyyk6j">{n.name}</td>
                    <td className="whitespace-nowrap px-3 py-2.5" data-id="element-1384" data-mp="2epnscj2qwo3o">
                      <Link to={`/countries/${n.cca3}`} className="inline-flex items-center gap-2 text-muted hover:text-machine" data-id="element-1385" data-mp="20qo1r5vo9z4v">
                        <img src={`https://flagcdn.com/${n.cca2.toLowerCase()}.svg`} alt="" loading="lazy" className="h-3.5 w-5 rounded-[2px] object-cover ring-1 ring-line" data-id="element-1386" data-mp="25q0prtya5c91" />
                        {n.country}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono text-[12.5px] tabular text-muted" data-id="element-1387" data-mp="1h78m6gpm8qd2">{share != null ? `${share.toFixed(0)}%` : '—'}</td>
                    <td className="px-3 py-2.5" data-id="element-1388" data-mp="1r1quy09w3c3q">
                      <div className="flex items-center gap-3" data-id="element-1389" data-mp="252be4xjpv9xr">
                        <div className="h-1.5 flex-1 rounded-full bg-raised" aria-hidden="true" data-id="element-1390" data-mp="250qn3hwm294n">
                          <div className="h-full rounded-full bg-human/75" style={{
                        width: `${n.users / max * 100}%`
                      }} data-id="element-1391" data-mp="201npgiiyn56p" />
                        </div>
                        <span className="w-20 shrink-0 text-right font-mono text-[12.5px] tabular text-ink" title={`${formatInt(n.users)} users`} data-id="element-1392" data-mp="29ul0y4pjbtvt">
                          {formatCompact(n.users)}
                          <SourceRef id="apnicAspop" href={aspopUrl(n.cca2)} estimate={{
                        low: formatCompact(n.users * 0.85),
                        high: formatCompact(n.users * 1.15)
                      }} figure={n.asn} data-id="element-1393" data-mp="2jsr1oizlutz3" />
                        </span>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[12px] text-muted" data-id="element-1394" data-mp="812g3wdvtcrh">User counts are rounded APNIC estimates. Every country page has its live top-10 networks.</p>
      </section>
    </>;
}